/**
 * Warenkorb-Ablauf — Sicherheitsnetze und Feinschliff rund um „In den Warenkorb" (2026-09).
 *
 * Analyse 2026-09-05 auf DEV 200344666447 (Details: _cart_flow_2026-09/README.md):
 *
 *  1) Candy Rack faengt den Klick auf den Kaufbutton ab, holt seine Angebote (~1 s) und zeigt erst dann
 *     sein Popup. Solange bekommt der Kunde keine Rueckmeldung, der Button wirkt tot. Hier bekommt der
 *     geklickte Button den Theme-Ladezustand (.is-loading), bis das Popup steht oder geschlossen ist.
 *
 *  2) Candy Rack sperrt die Seite hinter dem Popup mit body.candyrack-body-open (position: fixed) und
 *     hebt die Sperre mit classList.toggle() auf. Das Sonderskript in der Candy-Rack-Konfiguration
 *     („Click-out behaves as close", APPS-7944) schliesst das Popup beim Klick auf den Hintergrund ein
 *     zweites Mal (Candy Rack macht das selbst schon) und haengt bei jedem weiteren Popup einen weiteren
 *     Handler an dasselbe iframe. Gerade Anzahl Toggles = Sperre bleibt = Seite scrollt nicht mehr.
 *     Dieses Netz prueft nach „candyrack-closed", ob die Sperre bei geschlossenem Popup haengen blieb,
 *     und loest sie. Die eigentliche Ursache gehoert in der Candy-Rack-Konfiguration behoben.
 *
 *  4) Candy Rack spricht beim Klick zwei kalte Hosts an (Angebote-API, Storefront-API). Ein Preconnect im
 *     <head> nuetzt nichts, Chrome schliesst ungenutzte Verbindungen nach ~10 s. Deshalb wird die Verbindung
 *     erst aufgebaut, wenn der Kunde den Kaufbutton anvisiert (pointerenter/touchstart/focus) — dann steht
 *     sie beim Klick. Spart den DNS/TLS-Aufbau (~50-150 ms) vor der ersten Antwort.
 *
 *  3) custom.css setzt html { scroll-behavior: smooth }. Theme-Drawer und -Modals (side-drawer.js,
 *     modal.js, main.js) und Candy Rack stellen die Scrollposition beim Schliessen mit
 *     window.scrollTo(0, y) bzw. scrollBy(0, y) wieder her. In dieser Legacy-Signatur greift die
 *     CSS-Regel: die Seite springt zum Seitenanfang und rutscht animiert zurueck. Legacy-Aufrufe laufen
 *     deshalb sofort; Options-Aufrufe (scrollTo({ behavior: 'smooth' })) und Ankerlinks bleiben weich.
 *
 * Kein Liquid, keine Abhaengigkeit zu main.js — laeuft auf jeder Seite mit Kaufbutton (PDP, Quick-Add).
 */
(() => {
  'use strict';

  // ── 3) Scroll-Wiederherstellung ohne Animation ──────────────────────────────────────────────
  ['scrollTo', 'scrollBy'].forEach((name) => {
    const native = window[name];
    if (typeof native !== 'function') return;
    window[name] = function d1InstantLegacyScroll(x, y) {
      if (arguments.length === 2 && typeof x === 'number' && typeof y === 'number'
        && getComputedStyle(document.documentElement).scrollBehavior === 'smooth') {
        return native.call(window, { left: x, top: y, behavior: 'instant' });
      }
      return native.apply(window, arguments);
    };
  });

  // ── 4) Verbindungen zu den Candy-Rack-Hosts kurz vor dem Klick aufbauen ─────────────────────
  const PRECONNECT_HOSTS = ['https://candyrack.digismoothie.app', 'https://b40fdd-5.myshopify.com'];
  let lastPreconnect = 0;
  const preconnect = (evt) => {
    const t = evt.target;
    if (!t || !t.closest || !t.closest('[name="add"]')) return;
    const now = Date.now();
    if (now - lastPreconnect < 8000) return; // Chrome haelt die Verbindung ~10 s offen
    lastPreconnect = now;
    document.querySelectorAll('link[data-d1-preconnect]').forEach((l) => l.remove());
    PRECONNECT_HOSTS.forEach((href) => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      link.crossOrigin = 'anonymous';
      link.dataset.d1Preconnect = '';
      document.head.appendChild(link);
    });
  };
  ['pointerenter', 'touchstart', 'focusin'].forEach((n) => document.addEventListener(n, preconnect, { capture: true, passive: true }));

  // ── 1) + 2) Candy Rack ──────────────────────────────────────────────────────────────────────
  const LOCK_CLASS = 'candyrack-body-open';
  let lastAtcButton = null;
  let savedScrollY = 0;
  let loadingButton = null;
  let loadingTimer = 0;

  // Fruehestmoeglicher Zeitpunkt: Capture-Phase am window, vor Candy Racks Document-Listener.
  window.addEventListener('click', (evt) => {
    const btn = evt.target && evt.target.closest ? evt.target.closest('[name="add"]') : null;
    if (btn) lastAtcButton = btn;
  }, true);

  const stopLoading = () => {
    clearTimeout(loadingTimer);
    if (loadingButton) loadingButton.classList.remove('is-loading');
    loadingButton = null;
  };

  const startLoading = (btn) => {
    // aria-disabled setzt product-form.js waehrend des eigenen Ablaufs — dann nichts anfassen.
    if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
    stopLoading();
    loadingButton = btn;
    btn.classList.add('is-loading');
    // Notausgang, falls weder Popup noch Schliessen kommt (z. B. Candy Rack antwortet nicht).
    loadingTimer = setTimeout(() => {
      if (loadingButton && loadingButton.getAttribute('aria-disabled') !== 'true') stopLoading();
    }, 6000);
  };

  // Nach „candyrack-closed" ist das Popup zu — eine danach (wieder) gesetzte Sperre ist der haengende Zustand.
  // Der zweite Close-Handler laeuft bis zu einigen hundert Millisekunden nach dem ersten, deshalb mehrere
  // Pruefzeitpunkte; ein neuer Klick auf den Kaufbutton (neues Popup) raeumt die Timer ab.
  let releaseTimers = [];
  const clearReleaseTimers = () => { releaseTimers.forEach(clearTimeout); releaseTimers = []; };

  const releaseLock = () => {
    const { body } = document;
    if (!body.classList.contains(LOCK_CLASS)) return;
    body.classList.remove(LOCK_CLASS);
    body.style.removeProperty('top');
    const drawer = document.querySelector('cart-drawer');
    if (body.classList.contains('fixed')) {
      // Der Theme-Drawer hat inzwischen geoeffnet und dabei scrollY = 0 gesehen (Seite war gesperrt).
      // Position nachtragen, damit SideDrawer.close() (scrollTo(0, this.scrollY)) an die richtige Stelle geht.
      if (drawer && drawer.hasAttribute('open') && drawer.scrollY === 0) {
        drawer.scrollY = savedScrollY;
        body.style.top = `-${savedScrollY}px`;
      }
    } else {
      window.scrollTo({ top: savedScrollY, behavior: 'instant' });
    }
  };

  // Popup steht (Sperre gesetzt) → Ladezustand am Button beenden.
  new MutationObserver(() => {
    if (document.body.classList.contains(LOCK_CLASS)) stopLoading();
  }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

  document.addEventListener('addToCartButtonClicked', (evt) => {
    clearReleaseTimers();
    savedScrollY = window.scrollY;
    const fromEvent = evt.detail && evt.detail.button instanceof Element ? evt.detail.button : null;
    startLoading(fromEvent || lastAtcButton);
  });

  document.addEventListener('candyrack-closed', () => {
    stopLoading();
    clearReleaseTimers();
    [0, 150, 400, 800, 1500, 2500].forEach((ms) => releaseTimers.push(setTimeout(releaseLock, ms)));
  });
})();
